import React, { useState, useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { CardContainer, CardBody, CardItem } from '@components/ui/3d-card'
import { Heart, Clock, Rocket, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// Pre-generate blur data URL for better performance
const BLUR_DATA_URL =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjFmMWYxIi8+PC9zdmc+'

const ModelItem = ({ id, name, alias, description, eta, image, isFavorite, run_count, type, toggleFavorite }) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  // Memoize processing time calculation
  const processTime = useMemo(() => {
    if (!eta) return '23 sec'
    const min = Math.floor(eta / 60) ? `${Math.floor(eta / 60)} min ` : ''
    const sec = eta % 60 ? `${eta % 60} sec` : ''
    return `${min}${sec}`
  }, [eta])

  // Memoize formatted run count
  const formattedRunCount = useMemo(() => run_count?.toLocaleString(), [run_count])

  // Memoize image sizes for better performance
  const imageSizes = useMemo(() => {
    if (type === 'list') {
      return '(max-width: 640px) 20vw, (max-width: 768px) 25vw, 33vw'
    }
    return '(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw'
  }, [type])

  // Memoize image container class
  const imageContainerClass = useMemo(
    () =>
      cn(
        'relative overflow-hidden rounded-lg transition-all duration-700',
        type === 'list' ? 'h-24 w-24 flex-shrink-0' : 'aspect-[4/3] w-full rounded-t-xl',
        isHovering && type !== 'list' ? 'scale-110' : 'scale-100'
      ),
    [type, isHovering]
  )

  // Memoize image class
  const imageClass = useMemo(
    () => cn('object-cover transition-all duration-700', !imageLoaded ? 'opacity-0' : 'opacity-100'),
    [imageLoaded]
  )

  // Handle image load
  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  // Handle favorite toggle
  const handleFavoriteToggle = (e) => {
    e.preventDefault()
    e.stopPropagation()
    toggleFavorite(e, id, isFavorite, name)
  }

  // List view
  if (type === 'list') {
    return (
      <div className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4 px-2 mb-4">
        <Link
          href={{
            pathname: '/model/[name]',
            query: { name: alias },
          }}
          passHref
          className="block"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer flex gap-4 items-center p-4 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 overflow-hidden"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className={imageContainerClass}>
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 animate-pulse rounded-lg">
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-white/10 to-transparent animate-shimmer"
                    style={{ backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }}
                  ></div>
                </div>
              )}
              <Image
                src={image || '/placeholder.svg'}
                alt={name}
                fill
                sizes={imageSizes}
                className={imageClass}
                loading="lazy"
                placeholder="blur"
                blurDataURL={BLUR_DATA_URL}
                onLoadingComplete={handleImageLoad}
              />
            </div>

            <div className="text-content flex-1 min-w-0">
              {!imageLoaded ? (
                <>
                  <div className="h-5 w-3/4 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded mb-2 animate-pulse"></div>
                  <div className="h-3 w-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded mb-1 animate-pulse"></div>
                  <div className="h-3 w-2/3 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded mb-3 animate-pulse"></div>
                  <div className="flex gap-3">
                    <div className="h-4 w-16 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                    <div className="h-4 w-16 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-left font-semibold text-sm sm:text-base md:text-lg text-gray-800 dark:text-white mb-1 truncate">
                    {name}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{description}</p>

                  <div className="flex gap-2 sm:gap-3 mt-2 flex-wrap">
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <Rocket size={12} className="text-gray-400" />
                      <span>{formattedRunCount} runs</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <Clock size={12} className="text-gray-400" />
                      <span>{processTime}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div onClick={handleFavoriteToggle} className="ml-2 flex-shrink-0">
              <Heart
                size={20}
                className={cn(
                  'transition-colors duration-300',
                  isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400 dark:text-gray-500'
                )}
              />
            </div>
          </div>
        </Link>
      </div>
    )
  }

  // Grid view
  return (
    <div className="model-card w-[46%] relative md:!w-[25%] lg:!w-[18%]">
      <CardContainer containerClassName="py-4 h-full" className="w-full">
        <CardBody className="h-full w-full rounded-xl bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 flex flex-col overflow-hidden">
          <Link
            href={{
              pathname: '/model/[name]',
              query: { name: alias },
            }}
            passHref
            className="w-full flex-1 flex flex-col"
          >
            {/* Image section */}
            <CardItem translateZ={20} className="w-full">
              <div className={imageContainerClass}>
                {!imageLoaded && (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-t-xl">
                    {/* Shimmering effect */}
                    <div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-white/10 to-transparent animate-shimmer"
                      style={{ backgroundSize: '200% 100%', animation: 'shimmer 2s infinite' }}
                    ></div>

                    {/* Subtle floating shapes for a modern look */}
                    <div className="absolute top-1/4 left-1/4 w-12 h-12 rounded-full bg-gradient-to-br from-purple-400/20 to-indigo-500/20 animate-float-slow"></div>
                    <div className="absolute bottom-1/3 right-1/3 w-16 h-16 rounded-full bg-gradient-to-br from-indigo-400/20 to-purple-500/20 animate-float-medium"></div>
                    <div className="absolute top-1/2 right-1/4 w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400/20 to-indigo-500/20 animate-float-fast"></div>
                  </div>
                )}

                <Image
                  src={image || '/placeholder.svg'}
                  fill
                  sizes={imageSizes}
                  priority={false}
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL={BLUR_DATA_URL}
                  alt={alias}
                  className={imageClass}
                  onLoadingComplete={handleImageLoad}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Favorite button */}
                <CardItem
                  as="div"
                  translateZ={50}
                  className="absolute top-3 right-3 z-10"
                  onClick={handleFavoriteToggle}
                >
                  <div className="w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-black/40 transition-all duration-300 cursor-pointer border border-white/20">
                    <Heart
                      size={18}
                      className={cn(
                        'transition-colors duration-300',
                        isFavorite ? 'fill-red-500 text-red-500' : 'text-white'
                      )}
                    />
                  </div>
                </CardItem>

                {/* Stats badges */}
                <CardItem as="div" translateZ={40} className="absolute bottom-3 left-3 z-10 flex gap-2">
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 backdrop-blur-md text-white text-xs border border-white/20">
                    <Rocket size={12} className="text-white" />
                    <span>{formattedRunCount}</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/40 backdrop-blur-md text-white text-xs border border-white/20">
                    <Clock size={12} className="text-white" />
                    <span>{processTime}</span>
                  </div>
                </CardItem>
              </div>
            </CardItem>

            {/* Content section */}
            <div className="flex-1 flex flex-col">
              <CardItem translateZ={40} className="w-full flex-1 flex flex-col">
                <div className="p-4 flex flex-col flex-1">
                  {!imageLoaded ? (
                    <>
                      <div className="h-5 w-3/4 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded mb-3 animate-pulse"></div>
                      <div className="h-3 w-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded mb-2 animate-pulse"></div>
                      <div className="h-3 w-5/6 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded mb-4 animate-pulse"></div>

                      <div className="mt-auto flex justify-between items-center">
                        <div className="h-4 w-16 bg-gradient-to-r from-purple-400/30 to-purple-500/30 dark:from-purple-500/30 dark:to-purple-600/30 rounded animate-pulse"></div>
                        <div className="h-8 w-16 rounded-full bg-gradient-to-r from-purple-400/50 to-indigo-500/50 dark:from-purple-500/40 dark:to-indigo-600/40 animate-pulse"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="font-bold text-gray-800 dark:text-white text-sm sm:text-base md:text-lg mb-2 line-clamp-1">
                        {name}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-300 line-clamp-2 text-xs lg:text-sm mb-4 flex-1">
                        {description}
                      </p>

                      <div className="flex justify-between items-center mt-auto">
                        <CardItem
                          as="div"
                          translateZ={60}
                          className="text-xs whitespace-nowrap mr-2 lg:text-sm font-medium flex items-center gap-1 text-purple-600 dark:text-purple-400"
                        >
                          Try now <ArrowRight size={14} />
                        </CardItem>

                        <Link
                          href={{
                            pathname: '/model/[name]',
                            query: { name: alias, api: 'python' },
                          }}
                          passHref
                        >
                          <CardItem translateZ={60}>
                            <button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs lg:text-sm py-2 px-4 rounded-full hover:shadow-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 whitespace-nowrap">
                              API
                            </button>
                          </CardItem>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </CardItem>
            </div>
          </Link>
        </CardBody>
      </CardContainer>
    </div>
  )
}

export default ModelItem
